import React, { useState, useEffect } from 'react';
import {
  View,
  StyleSheet,
  FlatList,
  Pressable,
  Image,
  ActivityIndicator,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { Text, Theme } from '../../../components/ui';
import { auth } from '../../../config/firebase';
import { PrivateProfileService } from '../../../services/privateProfileService';
import { BookService } from '../../../services/bookService';
import { FireBook } from '../../../models/FirestoreModels';
import EmptyBookPlaceholder from './EmptyBookPlaceholder';

const BOOK_COVER_WIDTH = 120;
const BOOK_COVER_HEIGHT = 180;

/**
 * My Reading List Section Component
 * Displays user's book collection with empty state placeholder at the end
 */
const MyReadingListSection: React.FC = () => {
  const navigation = useNavigation();
  const [books, setBooks] = useState<FireBook[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadUserBooks();
    
    // Refresh when screen comes into focus
    const unsubscribe = navigation.addListener('focus', () => {
      loadUserBooks();
    });
    
    return unsubscribe;
  }, [navigation]);

  const loadUserBooks = async () => {
    try {
      setLoading(true);
      const user = auth.currentUser;
      if (!user) {
        setLoading(false);
        return;
      }

      console.log('[MyReadingList] Loading favorite books for user:', user.uid);

      // Use PrivateProfileService to get profile
      const result = await PrivateProfileService.getProfile(user.uid);
      
      if (!result.success || !result.data) {
        console.log('[MyReadingList] Failed to load profile:', result.error);
        setLoading(false);
        return;
      }

      const booksProgress = result.data.booksProgress || {};

      // Filter for ONLY favorite books and get their IDs with timestamps
      const favoriteBooks = Object.entries(booksProgress)
        .filter(([_, progress]) => progress.isFavorite === true)
        .map(([bookId, progress]) => ({
          bookId,
          favoriteAddedAt: progress.favoriteAddedAt || 0,
        }));

      console.log('[MyReadingList] Found', favoriteBooks.length, 'favorite books');

      if (favoriteBooks.length === 0) {
        setLoading(false);
        return;
      }

      // Sort by newest first (highest favoriteAddedAt timestamp)
      favoriteBooks.sort((a, b) => b.favoriteAddedAt - a.favoriteAddedAt);

        // Batch fetch books (fast, uses cache, no insights needed for list view)
        const bookIds = favoriteBooks.map(({ bookId }) => bookId);
        const booksResult = await BookService.getBooksById(bookIds, 'en');
        
        let validBooks: FireBook[] = [];
        if (booksResult.success && booksResult.data) {
          validBooks = booksResult.data;
        } else {
          console.warn(`[MyReadingList] Failed to fetch books:`, booksResult.error);
        }

      console.log('[MyReadingList] Loaded', validBooks.length, 'books');
      setBooks(validBooks);
    } catch (error) {
      console.error('[MyReadingList] Error loading user books:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBookPress = (book: FireBook) => {
    const bookId = book.id.split('_')[0];
    (navigation as any).navigate('BookDetail', { bookId });
  };

  const handleAddBookPress = () => {
    (navigation as any).navigate('Explore');
  };

  const handleSeeAllPress = () => {
    // Navigate to MyReadingList screen
    (navigation as any).navigate('MyReadingList');
  };

  const renderBookCover = ({ item }: { item: FireBook }) => (
    <Pressable
      style={styles.bookContainer}
      onPress={() => handleBookPress(item)}
      testID="book-item"
    >
      <Image
        source={{ uri: item.coverImageUrl || item.coverImageFullUrl }}
        style={styles.bookCover}
        resizeMode="cover"
      />
      <Text style={styles.bookTitle} numberOfLines={2}>
        {item.title}
      </Text>
    </Pressable>
  );

  const renderEmptyPlaceholder = () => (
    <EmptyBookPlaceholder onPress={handleAddBookPress} />
  );

  if (loading) {
    return (
      <View style={styles.container}>
        <View style={styles.header}>
          <Text style={styles.headerTitle}>My Reading List</Text>
        </View>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="small" color={Theme.colors.primary} />
        </View>
      </View>
    );
  }

  if (books.length === 0) {
    return null;
  }

  return (
    <View style={styles.container}>
      {/* Section Header */}
      <View style={styles.header}>
        <Text style={styles.headerTitle}>My Reading List</Text>
        {books.length > 0 && (
          <Pressable onPress={handleSeeAllPress}>
            <Text style={styles.seeAllText}>See All</Text>
          </Pressable>
        )}
      </View>

      {/* Horizontal Scrollable List */}
      <FlatList
        data={books}
        renderItem={renderBookCover}
        keyExtractor={(item) => item.id}
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.listContent}
        ListFooterComponent={renderEmptyPlaceholder}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: 40,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: Theme.spacing.lg,
    marginBottom: Theme.spacing.md,
  },
  headerTitle: {
    fontSize: 24,
    fontFamily: Theme.typography.fontFamily.bold,
    color: Theme.colors.sectionHeader,
    lineHeight: 32,
  },
  seeAllText: {
    fontSize: 14,
    fontFamily: Theme.typography.fontFamily.medium,
    color: Theme.colors.primary,
  },
  listContent: {
    paddingHorizontal: Theme.spacing.lg,
  },
  loadingContainer: {
    height: 200,
    justifyContent: 'center',
    alignItems: 'center',
  },
  bookContainer: {
    width: BOOK_COVER_WIDTH,
    marginRight: Theme.spacing.md,
  },
  bookCover: {
    width: BOOK_COVER_WIDTH,
    height: BOOK_COVER_HEIGHT,
    borderRadius: Theme.borderRadius.md,
    backgroundColor: Theme.colors.surface,
    marginBottom: Theme.spacing.sm,
  },
  bookTitle: {
    fontSize: 12,
    fontFamily: Theme.typography.fontFamily.medium,
    color: Theme.colors.textPrimary,
    textAlign: 'center',
    lineHeight: 16,
  },
});

export default MyReadingListSection;
